/**
 * Shared "initial + deterministic color" avatar fallback for a person without a profile picture.
 * Lives in `src/lib/color` since it started as an ava-specific helper before a second feature
 * needed the identical logic — two implementations of the same hash-to-palette pick drift silently.
 */

const AVATAR_COLORS = [
  "bg-indigo-200",
  "bg-blue-200",
  "bg-purple-200",
  "bg-pink-200",
  "bg-red-200",
  "bg-orange-200",
  "bg-amber-200",
  "bg-yellow-200",
  "bg-lime-200",
  "bg-green-200",
  "bg-emerald-200",
  "bg-teal-200",
  "bg-cyan-200",
  "bg-sky-200",
  "bg-violet-200",
  "bg-fuchsia-200",
];

/**
 * Get the initial letter from a person's name or email.
 * @param name - The person's display name.
 * @param email - The person's email address.
 * @returns The initial letter in uppercase, or "?" when both are empty.
 */
export function getInitial(name: string | null | undefined, email: string | null | undefined): string {
  return (name?.[0] || email?.[0] || "?").toUpperCase();
}

/**
 * Get a consistent Tailwind background color for a person based on their name or email.
 * @param name - The person's display name.
 * @param email - The person's email address.
 * @returns A Tailwind CSS background color class.
 */
export function getAvatarColor(name: string | null | undefined, email: string | null | undefined): string {
  const identifier = name || email || "unknown";

  const hash = identifier.split("").reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}
